/**
 * Precedence Rank Selector Component
 * Part B - UX Simplification: Preset scale instead of numeric input
 * 
 * Presets:
 * - Authoritative = 1000
 * - Primary = 900
 * - Primary-Addendum = 800
 * - Secondary = 600
 * - Secondary-Low = 500
 * - Tertiary = 300
 * - Tertiary-Low = 200
 */
import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { ChevronDown, ChevronUp, Settings } from 'lucide-react';

// Preset definitions
const PRECEDENCE_PRESETS = [
  { label: 'Authoritative', value: 1000, description: 'Definitive source (e.g., official guide)', color: 'bg-purple-100 text-purple-800 border-purple-300' },
  { label: 'Primary', value: 900, description: 'Primary official source', color: 'bg-green-100 text-green-800 border-green-300' },
  { label: 'Primary-Addendum', value: 800, description: 'Supplementary to primary', color: 'bg-green-50 text-green-700 border-green-200' },
  { label: 'Secondary', value: 600, description: 'Certified/verified source', color: 'bg-blue-100 text-blue-800 border-blue-300' },
  { label: 'Secondary-Low', value: 500, description: 'Lower-priority secondary', color: 'bg-blue-50 text-blue-700 border-blue-200' },
  { label: 'Tertiary', value: 300, description: 'Unverified/community source', color: 'bg-gray-100 text-gray-800 border-gray-300' },
  { label: 'Tertiary-Low', value: 200, description: 'Lowest priority', color: 'bg-gray-50 text-gray-600 border-gray-200' },
];

// Find preset by value (exact or nearest)
const findPresetByValue = (value) => {
  // First try exact match
  const exactMatch = PRECEDENCE_PRESETS.find(p => p.value === value);
  if (exactMatch) return exactMatch;
  
  // Otherwise find nearest
  let nearest = PRECEDENCE_PRESETS[0];
  let minDiff = Math.abs(value - nearest.value);
  
  for (const preset of PRECEDENCE_PRESETS) {
    const diff = Math.abs(value - preset.value);
    if (diff < minDiff) {
      minDiff = diff;
      nearest = preset;
    }
  }
  
  return nearest;
};

// Check if value matches any preset exactly
const isPresetValue = (value) => {
  return PRECEDENCE_PRESETS.some(p => p.value === value);
};

export const PrecedenceRankSelector = ({ 
  value = 900, 
  onChange, 
  tier = 'PRIMARY',
  disabled = false,
  showCustomToggle = true 
}) => {
  const [showCustom, setShowCustom] = useState(!isPresetValue(value));
  const [customValue, setCustomValue] = useState(value);
  
  // Update custom value when value prop changes
  useEffect(() => {
    setCustomValue(value);
    setShowCustom(!isPresetValue(value));
  }, [value]);
  
  // Get current preset (for display)
  const currentPreset = findPresetByValue(value);
  const isCustom = !isPresetValue(value);
  
  // Handle preset selection
  const handlePresetClick = (presetValue) => {
    setCustomValue(presetValue);
    onChange(presetValue);
    setShowCustom(false);
  };
  
  // Handle custom value change
  const handleCustomChange = (e) => {
    const newValue = parseInt(e.target.value, 10);
    if (!isNaN(newValue) && newValue >= 1 && newValue <= 1000) {
      setCustomValue(newValue);
      onChange(newValue);
    }
  };
  
  // Filter presets by tier (optional: show recommended for tier)
  const getRecommendedForTier = () => {
    switch (tier) {
      case 'PRIMARY':
        return [1000, 900, 800];
      case 'SECONDARY':
        return [600, 500];
      case 'TERTIARY':
        return [300, 200];
      default:
        return [];
    }
  };
  
  const recommendedValues = getRecommendedForTier();
  
  return (
    <div className="space-y-3" data-testid="precedence-rank-selector">
      {/* Current Value Display */}
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">Precedence Rank</Label>
        <div className="flex items-center gap-2">
          <Badge className={currentPreset.color}>
            {currentPreset.label}
          </Badge>
          <span className="text-sm text-gray-500">({value})</span>
        </div>
      </div>
      
      {/* Preset Buttons */}
      {!showCustom && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {PRECEDENCE_PRESETS.map((preset) => {
            const isRecommended = recommendedValues.includes(preset.value);
            const isSelected = value === preset.value;
            
            return (
              <button
                key={preset.value}
                type="button"
                disabled={disabled}
                onClick={() => handlePresetClick(preset.value)}
                className={`
                  relative p-2 rounded-lg border-2 text-left transition-all
                  ${isSelected 
                    ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200' 
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }
                  ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                `}
                data-testid={`preset-${preset.value}`}
              >
                <div className="flex flex-col">
                  <span className={`font-medium text-sm ${isSelected ? 'text-blue-700' : 'text-gray-700'}`}>
                    {preset.label}
                  </span>
                  <span className="text-xs text-gray-500">{preset.value}</span>
                </div>
                {isRecommended && !isSelected && (
                  <span className="absolute top-1 right-1 w-2 h-2 bg-green-400 rounded-full" 
                        title={`Recommended for ${tier}`} />
                )}
              </button>
            );
          })}
        </div>
      )}
      
      {/* Custom Input */}
      {showCustom && (
        <div className="flex items-center gap-3">
          <Input
            type="number"
            min={1}
            max={1000}
            value={customValue}
            onChange={handleCustomChange}
            disabled={disabled}
            className="w-24"
            data-testid="custom-precedence-input"
          />
          <span className="text-sm text-gray-500">
            (1-1000, higher = stronger)
          </span>
        </div>
      )}
      
      {/* Toggle Custom/Preset */}
      {showCustomToggle && (
        <button
          type="button"
          onClick={() => setShowCustom(!showCustom)}
          disabled={disabled}
          className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
          data-testid="toggle-custom-mode"
        >
          <Settings className="w-3 h-3" />
          {showCustom ? 'Use presets' : 'Custom value'}
          {showCustom ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </button>
      )}
      
      {/* Hint */}
      {!showCustom && (
        <p className="text-xs text-gray-400">
          {currentPreset.description}
        </p>
      )}
    </div>
  );
};

export default PrecedenceRankSelector;
